
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
  Chip,
  Skeleton,
  useTheme,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  ViewModule as ViewModuleIcon,
  ColorLens as ColorLensIcon
} from '@mui/icons-material';
import URLManager from '../funcionalidadeNavbar/URLManager';
import './CatalogoVirtual.css';

const CatalogoVirtual = ({ open, onClose, fullPage = false }) => { 
  const _url = URLManager.getBaseURL();
  const theme = useTheme();
  
  // Estados principais
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  // Estados de navegação hierárquica
  const [visualizacao, setVisualizacao] = useState('modelos'); // 'modelos' ou 'variacoes'
  const [modeloSelecionado, setModeloSelecionado] = useState(null);
  const [variacoesCores, setVariacoesCores] = useState([]);
  
  // Estados de filtros
  const [marca, setMarca] = useState('');
  const [cor, setCor] = useState('');
  const [status, setStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para opções de filtros
  const [marcasDisponiveis, setMarcasDisponiveis] = useState([]);
  const [coresDisponiveis, setCoresDisponiveis] = useState([]);
  
  // Estados do modal de detalhes
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Removidos: productDetails, detailsLoading (não utilizados)
  
  // Estados de zoom
  const [zoomedImage, setZoomedImage] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(200); // Nível de zoom inicial (200% = 2x)

  // Função para carregar variações de cor de um modelo (produto pai)
  const carregarVariacoesCor = useCallback(async (idProdutoPai) => {
    setLoading(true);
    try {
      // Buscar o produto pai (modelo original)
      const produtoPaiResponse = await fetch(`${_url}/produto/${idProdutoPai}`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
        }
      });
      let produtoPai = null;
      if (produtoPaiResponse.ok) {
        produtoPai = await produtoPaiResponse.json();
        produtoPai = {
          ...produtoPai,
          cor: 'Original',
          isProdutoPai: true,
          imagemPrincipal: produtoPai.image || '/placeholder-product.svg'
        };
      }

      // Buscar variações de cor
      const variacoesResponse = await fetch(`${_url}/produto/${idProdutoPai}/variacoes`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
        }
      });
      let variacoes = [];
      if (variacoesResponse.ok) {
        variacoes = await variacoesResponse.json();
        variacoes = variacoes.map(v => ({
          ...v,
          isProdutoPai: false,
          imagemPrincipal: v.image || '/placeholder-product.svg'
        }));
      }

      // Aplicar filtro de cor, se houver
      let todasVariacoes = [];
      if (produtoPai) {
        todasVariacoes = [produtoPai, ...variacoes];
      } else {
        todasVariacoes = variacoes;
      }
      if (cor) {
        todasVariacoes = todasVariacoes.filter(v => {
          if (cor === 'Original') return v.isProdutoPai;
          return v.cor && v.cor.toLowerCase() === cor.toLowerCase();
        });
      }
      setVariacoesCores(todasVariacoes);
    } catch (error) {
      setVariacoesCores([]);
    } finally {
      setLoading(false);
    }
  }, [_url, cor]);

  // Função para carregar modelos únicos do catálogo (produtos pais)
  const carregarModelos = useCallback(async (pageNum = 0) => {
    setLoading(true);
    try {
      // Primeiro tenta buscar produtos pais (modelos únicos) do seu sistema
      let response = await fetch(`${_url}/produto/so-pais`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
        }
      });

      // Se o endpoint /produto/so-pais não funcionar, usa o endpoint geral
      if (!response.ok) {
        response = await fetch(`${_url}/produto`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
          }
        });
        if (!response.ok) {
          throw new Error('Erro ao carregar produtos');
        }
      }

      const produtosPais = await response.json();

      // Garantir que apenas produtos pais sejam exibidos (filtrar por propriedades que indicam produto pai)
      let produtosSomenteModelos = produtosPais.filter(produto => {
        if (produto.hasOwnProperty('parentProductId')) {
          return !produto.parentProductId || produto.parentProductId === 0;
        }
        if (produto.hasOwnProperty('produtoPai') || produto.hasOwnProperty('produto_pai')) {
          const produtoPai = produto.produtoPai || produto.produto_pai;
          return !produtoPai;
        }
        if (produto.hasOwnProperty('idProdutoPai') || produto.hasOwnProperty('id_produto_pai')) {
          const idProdutoPai = produto.idProdutoPai || produto.id_produto_pai;
          return !idProdutoPai || idProdutoPai === 0;
        }
        if (produto.hasOwnProperty('parentId') || produto.hasOwnProperty('parent_id')) {
          const parentId = produto.parentId || produto.parent_id;
          return !parentId || parentId === 0;
        }
        return true;
      });

      // Agrupar por nomeModelo e marca para garantir que não haja duplicatas
      const modelosUnicos = produtosSomenteModelos.reduce((acc, produto) => {
        const key = `${produto.nomeModelo || 'sem-nome'}-${produto.marca || 'sem-marca'}`;
        if (!acc[key]) {
          acc[key] = produto;
        } else {
          const existente = acc[key];
          const produtoEhMelhorPai =
            (!produto.parentProductId && existente.parentProductId) ||
            (!produto.parentProductId && !existente.parentProductId && produto.idProduto < existente.idProduto);
          if (produtoEhMelhorPai) {
            acc[key] = produto;
          }
        }
        return acc;
      }, {});

      // Converter de volta para array
      const produtosPaisFiltrados = Object.values(modelosUnicos);

      // Filtrar produtos conforme os filtros aplicados
      let produtosFiltrados = produtosPaisFiltrados;
      if (marca) {
        produtosFiltrados = produtosFiltrados.filter(p =>
          p.marca && p.marca.toLowerCase().includes(marca.toLowerCase())
        );
      }
      if (status) {
        produtosFiltrados = produtosFiltrados.filter(p =>
          p.status && p.status.toLowerCase().includes(status.toLowerCase())
        );
      }
      if (searchTerm) {
        produtosFiltrados = produtosFiltrados.filter(p =>
          (p.nomeModelo && p.nomeModelo.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (p.referencia && p.referencia.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      // Para cada produto pai, buscar quantas variações ele tem
      const modelosComVariacoes = await Promise.all(
        produtosFiltrados.map(async (produto) => {
          try {
            const variacoesResponse = await fetch(`${_url}/produto/${produto.idProduto}/variacoes`, {
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
              }
            });
            if (variacoesResponse.ok) {
              const variacoes = await variacoesResponse.json();
              return {
                ...produto,
                totalVariacoes: variacoes.length,
                imagemPrincipal: produto.image || '/placeholder-product.svg'
              };
            } else {
              return {
                ...produto,
                totalVariacoes: 0,
                imagemPrincipal: produto.image || '/placeholder-product.svg'
              };
            }
          } catch (error) {
            return {
              ...produto,
              totalVariacoes: 0,
              imagemPrincipal: produto.image || '/placeholder-product.svg'
            };
          }
        })
      );

      setProdutos(modelosComVariacoes);
      setTotalPages(Math.ceil(modelosComVariacoes.length / 20));
      setTotalElements(modelosComVariacoes.length);
    } catch (error) {
      setProdutos([]);
    } finally {
      setLoading(false);
    }
  }, [_url, marca, status, searchTerm]);

  // Função para carregar opções de filtros
  const carregarOpcoesFiltros = useCallback(async () => {
    try {
      const response = await fetch(`${_url}/produto/so-pais`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
        }
      });

      if (response.ok) {
        const produtos = await response.json();

        // Extrair marcas únicas
        const marcas = [...new Set(produtos.map(p => p.marca).filter(Boolean))];
        setMarcasDisponiveis(marcas);

        // Se estivermos na visualização de variações, carregar cores disponíveis
        if (visualizacao === 'variacoes' && modeloSelecionado) {
          const variacoesResponse = await fetch(`${_url}/produto/${modeloSelecionado.idProduto}/variacoes`, {
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${sessionStorage.getItem('usuarioToken')}`
            }
          });

          if (variacoesResponse.ok) {
            const variacoes = await variacoesResponse.json();
            const cores = [...new Set(variacoes.map(v => v.cor).filter(Boolean))];
            // Adicionar "Original" para representar o produto pai
            const coresComOriginal = ['Original', ...cores];
            setCoresDisponiveis(coresComOriginal);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar opções de filtros:', error);
    }
  }, [_url, visualizacao, modeloSelecionado]);

  // Função para navegar para um modelo específico e ver suas variações
  const selecionarModelo = (modelo) => {
    setModeloSelecionado(modelo);
    setVisualizacao('variacoes');
    carregarVariacoesCor(modelo.idProduto); // Passa o ID do produto pai
  };

  // Função para voltar à visualização de modelos
  const voltarParaModelos = () => {
    setVisualizacao('modelos');
    setModeloSelecionado(null);
    setVariacoesCores([]);
    setCor(''); // Limpar filtro de cor
    setCoresDisponiveis([]); // Limpar opções de cores
    carregarModelos(0);
  };

  // Removida função carregarDetalhesProduto (não utilizada)

  useEffect(() => {
    // Carrega os dados se o modal estiver aberto ou se estivermos em modo fullPage
    if (open || fullPage) {
      carregarOpcoesFiltros();
      if (visualizacao === 'modelos') {
        carregarModelos(0);
      }
    }
  }, [open, fullPage, carregarModelos, carregarOpcoesFiltros, visualizacao]);

  // useEffect para carregar cores disponíveis quando selecionar um modelo
  useEffect(() => {
    if (visualizacao === 'variacoes' && modeloSelecionado) {
      carregarOpcoesFiltros();
    }
  }, [visualizacao, modeloSelecionado, carregarOpcoesFiltros]);

  // Função para aplicar filtros
  const aplicarFiltros = () => {
    setPage(0);
    if (visualizacao === 'modelos') {
      carregarModelos(0);
    } else {
      carregarVariacoesCor(modeloSelecionado.idProduto);
    }
  };

  // Função para abrir imagem ampliada (zoom)
  const handleOpenImageModal = (produto) => {
    setSelectedProduct(produto);
    setZoomedImage(produto.imagemPrincipal || produto.image || '/placeholder-product.svg');
    setZoomLevel(200);
    setMousePosition({ x: 50, y: 50 });
  };

  // Função para fechar o modal de imagem
  const handleCloseImageModal = () => {
    setSelectedProduct(null);
    setZoomedImage(null);
    setZoomLevel(200);
    setMousePosition({ x: 0, y: 0 });
  };

  // Função de hover da imagem - Zoom em toda a área da imagem (incluindo bordas)
  const handleImageHover = (imageUrl, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Calcular posição sem limitações para permitir zoom nas bordas
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    // Garantir que os valores estejam dentro do range válido (0-100%)
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    
    setZoomedImage(imageUrl);
    setMousePosition({ x: clampedX, y: clampedY });
  };

  // Função para sair do hover
  const handleImageLeave = () => {
    setZoomedImage(null);
    setMousePosition({ x: 0, y: 0 }); // Reset da posição
    setZoomLevel(200); // Resetar zoom para o padrão (200%)
  };

  // Função para controlar zoom com scroll do mouse
  const handleWheel = (event, imageUrl) => {
    event.preventDefault();
    
    // Ajustar o nível de zoom baseado na direção do scroll
    setZoomLevel(prevZoom => {
      const delta = event.deltaY > 0 ? -20 : 20; // Scroll para baixo diminui, para cima aumenta
      const newZoom = prevZoom + delta;
      
      // Limitar o zoom entre 100% (1x) e 500% (5x)
      return Math.max(100, Math.min(500, newZoom));
    });
    
    // Ativar o zoom na imagem se ainda não estiver ativo
    if (!zoomedImage) {
      setZoomedImage(imageUrl);
    }
  };

  // Função para renderizar imagem com zoom
  const renderImageWithZoom = (imageUrl, alt, isMain = false) => {
    // Imagem principal GIGANTE preenchendo toda a tela, variações normais
    const imageSize = isMain ? 
      { width: '100%', height: '100%' } : 
      { width: 150, height: 150 };
    
    return (
      <Box
        sx={{
          position: 'relative',
          width: isMain ? '100%' : imageSize.width,
          height: isMain ? '100%' : imageSize.height,
          maxWidth: isMain ? '100%' : imageSize.width,
          overflow: 'hidden',
          borderRadius: isMain ? 3 : 2,
          cursor: 'zoom-in',
          border: isMain ? `5px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
          boxShadow: isMain ? '0 12px 40px rgba(25, 118, 210, 0.3)' : theme.shadows[2],
          '&:hover': {
            boxShadow: isMain ? '0 20px 60px rgba(25, 118, 210, 0.4)' : theme.shadows[8],
            transform: isMain ? 'scale(1.01)' : 'scale(1.05)'
          },
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onMouseMove={(e) => handleImageHover(imageUrl, e)}
        onMouseLeave={handleImageLeave}
        onWheel={(e) => handleWheel(e, imageUrl)}
      >
        <img
          src={imageUrl}
          alt={alt}
          className={isMain ? 'imagem-principal-grande' : ''}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: isMain ? '#ffffff' : '#fafafa',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
        
        {/* Zoom overlay - Imagem ampliada sem transparência */}
        {zoomedImage === imageUrl && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: `${zoomLevel}%`, // Usa o nível de zoom dinâmico
              backgroundRepeat: 'no-repeat',
              backgroundPosition: `${mousePosition.x}% ${mousePosition.y}%`,
              pointerEvents: 'none',
              opacity: 1.0, // 100% opaco para ver todos os detalhes
              borderRadius: 'inherit',
              transition: 'background-position 0.05s ease, background-size 0.2s ease', // Transição suave para zoom
              border: '2px solid rgba(25, 118, 210, 0.3)', // Borda sutil para indicar zoom ativo
              boxShadow: 'inset 0 0 30px rgba(25, 118, 210, 0.1)', // Sombra azul sutil
              filter: 'contrast(1.05) brightness(1.02)', // Leve melhoria no contraste
              overflow: 'hidden' // Garantir que não ultrapasse os limites
            }}
          />
        )}
        
        {/* Indicador de zoom ativo com nível */}
        {zoomedImage === imageUrl && (
          <>
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                bgcolor: 'rgba(25, 118, 210, 0.9)',
                color: 'white',
                borderRadius: '4px',
                px: 1,
                py: 0.5,
                fontSize: '0.7rem',
                fontWeight: 'bold',
                pointerEvents: 'none',
                zIndex: 1
              }}
            >
              🔍 ZOOM {zoomLevel}%
            </Box>
            
            {/* Instruções de uso */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                borderRadius: '20px',
                px: 2,
                py: 1,
                fontSize: '0.75rem',
                fontWeight: 'normal',
                pointerEvents: 'none',
                zIndex: 1,
                display: 'flex',
                gap: 2,
                alignItems: 'center'
              }}
            >
              <span>🖱️ Mova o mouse para navegar</span>
              <span>|</span>
              <span>🔄 Role para ajustar zoom</span>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // Função para mudança de página
  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
    if (visualizacao === 'modelos') {
      carregarModelos(newPage - 1);
    }
  };

  // Se estiver em modo fullPage, renderizamos o conteúdo como página inteira
  if (fullPage) {
    return (
      <Box sx={{ p: 3, minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <ViewModuleIcon sx={{ fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            {visualizacao === 'modelos' ? '📦 Catálogo Virtual - Modelos' : `🎨 Variações - ${modeloSelecionado?.nomeModelo}`}
          </Typography>
          {onClose && (
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onClose}>
              Voltar
            </Button>
          )}
        </Box>

        <Box sx={{ p: 0 }}>
        {/* Breadcrumb de navegação */}
        {visualizacao === 'variacoes' && modeloSelecionado && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button 
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={voltarParaModelos}
                sx={{ bgcolor: 'white', color: 'primary.main' }}
              >
                Voltar aos Modelos
              </Button>
              <Typography variant="h6" sx={{ color: 'white', flexGrow: 1 }}>
                <ColorLensIcon sx={{ mr: 1 }} />
                Modelo: {modeloSelecionado.nomeModelo} - Variações de Cor
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                {variacoesCores.length} itens encontrados (incluindo original)
              </Typography>
            </Box>
          </Box>
        )}

        {/* Filtros */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Buscar"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Marca</InputLabel>
            <Select value={marca} onChange={(e) => setMarca(e.target.value)}>
              <MenuItem value="">Todas</MenuItem>
              {marcasDisponiveis.map((marcaItem) => (
                <MenuItem key={marcaItem} value={marcaItem}>
                  {marcaItem}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Ativo">Ativo</MenuItem>
              <MenuItem value="Inativo">Inativo</MenuItem>
            </Select>
          </FormControl>

          {visualizacao === 'variacoes' && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Cor</InputLabel>
              <Select value={cor} onChange={(e) => setCor(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {coresDisponiveis.map((corItem) => (
                  <MenuItem key={corItem} value={corItem}>
                    {corItem}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button variant="contained" onClick={aplicarFiltros}>
            Filtrar
          </Button>
        </Box>

        {/* Grid de produtos ou variações */}
        <Grid container spacing={2}>
          {loading ? (
            // Skeleton loading
            Array.from({ length: 8 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Card sx={{ height: 300 }}>
                  <Skeleton variant="rectangular" height={180} />
                  <CardContent>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={16} width="60%" />
                    <Skeleton variant="text" height={16} width="40%" />
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : visualizacao === 'modelos' ? (
            // Renderização de modelos
            produtos.map((produto) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={produto.idProduto}>
                <Card 
                  sx={{ 
                    height: 300, 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                      border: `2px solid ${theme.palette.primary.main}`
                    }
                  }}
                  onClick={() => selecionarModelo(produto)}
                >
                  <CardMedia
                    component="img"
                    height="180"
                    image={produto.imagemPrincipal || '/placeholder-product.svg'}
                    alt={produto.nomeModelo}
                    sx={{ objectFit: 'contain', p: 1 }}
                  />
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {produto.nomeModelo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Marca: {produto.marca}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip 
                        label={`${produto.totalVariacoes} cores`} 
                        color="primary" 
                        size="small"
                        icon={<PaletteIcon />}
                      />
                      <Chip 
                        label={produto.status} 
                        color="success" 
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            // Renderização de variações de cor (incluindo produto pai)
            variacoesCores.map((variacao) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={variacao.idProduto}>
                <Card 
                  sx={{ 
                    height: 320, 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: variacao.isProdutoPai 
                      ? `3px solid ${theme.palette.primary.main}` 
                      : '2px solid transparent',
                    boxShadow: variacao.isProdutoPai ? theme.shadows[4] : 'none',
                    background: variacao.isProdutoPai 
                      ? 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)' 
                      : 'white',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: variacao.isProdutoPai ? theme.shadows[12] : theme.shadows[8],
                      border: variacao.isProdutoPai 
                        ? `3px solid ${theme.palette.primary.dark}` 
                        : `2px solid ${theme.palette.secondary.main}`
                    }
                  }}
                  onClick={() => handleOpenImageModal(variacao)}
                >
                  {/* Badge de produto pai */}
                  {variacao.isProdutoPai && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 8, 
                      right: 8, 
                      zIndex: 1,
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: '12px',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      📦 ORIGINAL
                    </Box>
                  )}
                  
                  <CardMedia
                    component="img"
                    height="180"
                    image={variacao.imagemPrincipal || '/placeholder-product.svg'}
                    alt={`${variacao.nomeModelo} - ${variacao.cor}`}
                    sx={{ objectFit: 'contain', p: 1 }}
                  />
                  <CardContent>
                    <Typography variant="h6" sx={{ 
                      fontWeight: variacao.isProdutoPai ? 'bold' : 'normal', 
                      mb: 1,
                      color: variacao.isProdutoPai ? 'primary.main' : 'inherit'
                    }}>
                      {variacao.nomeModelo}
                      {variacao.isProdutoPai && ' (Original)'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Cor: {variacao.cor || 'Original'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Ref: {variacao.referencia || 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip 
                        label={variacao.cor || 'Original'} 
                        color={variacao.isProdutoPai ? 'primary' : 'secondary'} 
                        size="small"
                        icon={<PaletteIcon />}
                        variant={variacao.isProdutoPai ? 'filled' : 'outlined'}
                      />
                      <Chip 
                        label={variacao.status || 'Ativo'} 
                        color="success" 
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Paginação - apenas para modelos */}
        {visualizacao === 'modelos' && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}

        {/* Informações de total */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {visualizacao === 'modelos' 
              ? `Total: ${totalElements} modelos encontrados`
              : `Total: ${variacoesCores.length} itens do modelo ${modeloSelecionado?.nomeModelo} (incluindo original)`
            }
          </Typography>
        </Box>
      {/* Modal de imagem ampliada com zoom */}
      {selectedProduct && zoomedImage && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(0,0,0,0.92)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            cursor: 'zoom-out',
            p: 0,
            m: 0
          }}
          onClick={handleCloseImageModal}
        >
          <IconButton
            onClick={handleCloseImageModal}
            sx={{
              position: 'absolute',
              top: 24,
              right: 32,
              color: 'white',
              zIndex: 2100,
              background: 'rgba(0,0,0,0.3)',
              '&:hover': { background: 'rgba(0,0,0,0.5)' }
            }}
            size="large"
          >
            <CloseIcon sx={{ fontSize: 36 }} />
          </IconButton>
          <Box
            sx={{
              width: { xs: '90vw', md: '80vw', lg: '70vw' },
              height: { xs: '60vh', md: '80vh', lg: '85vh' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'white',
              borderRadius: 4,
              boxShadow: 24,
              overflow: 'hidden',
              position: 'relative',
              p: 0
            }}
            onClick={e => e.stopPropagation()}
          >
            {renderImageWithZoom(
              zoomedImage,
              selectedProduct.nomeModelo,
              true
            )}
          </Box>
          <Typography variant="h6" sx={{ color: 'white', mt: 2, textAlign: 'center' }}>
            {selectedProduct.nomeModelo} {selectedProduct.cor ? `- ${selectedProduct.cor}` : ''}
          </Typography>
        </Box>
      )}
        </Box>
      </Box>
    );
  }} 
export default CatalogoVirtual;